import { Groupement, User } from '../types/user';

export type AuthenticatedUserState = User & { logoUrl?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
    const value = record[key];
    return typeof value === 'number' ? value : undefined;
}

function readBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key];
    return typeof value === 'boolean' ? value : undefined;
}

function readRoles(record: Record<string, unknown>): readonly string[] {
    const value = record.roles;
    return Array.isArray(value)
        ? value.filter((role): role is string => typeof role === 'string')
        : [];
}

function readGroupement(record: Record<string, unknown>): Groupement | undefined {
    const value = record.groupement;
    if (!isRecord(value)) {
        return undefined;
    }

    const id = readNumber(value, 'id');
    const name = readString(value, 'name');

    if (id === undefined || name === undefined) {
        return undefined;
    }

    return {
        id,
        name,
        isGlobal: readBoolean(value, 'isGlobal') ?? false,
        logoUrl: readString(value, 'logoUrl'),
        displayLogoOnPublications: readBoolean(value, 'displayLogoOnPublications') ?? false,
    };
}

export function mapMeResponseToUserState(
    data: unknown,
    isSso: boolean,
): AuthenticatedUserState | null {
    if (!isRecord(data)) {
        return null;
    }

    const user = isRecord(data.user) ? data.user : data;
    const id = readNumber(user, 'id');
    const email = readString(user, 'email');

    if (id === undefined || email === undefined) {
        return null;
    }

    const quotaValue = user.quotaIA;

    return {
        id,
        myId: readString(user, 'userMyId') ?? readString(user, 'myId'),
        email,
        agency: readString(user, 'agencyName') ?? readString(user, 'agency'),
        quotaIA: typeof quotaValue === 'number' ? quotaValue : Number(quotaValue ?? 0),
        isSso,
        isAdmin: readRoles(user).includes('ROLE_ADMIN'),
        autoPublish: readBoolean(user, 'autoPublish') ?? false,
        displayLogoOnPublications: readBoolean(user, 'displayLogoOnPublications') ?? false,
        groupement: readGroupement(user),
    };
}

export const redirectToMyPrevisite = async (userId: string | null) => {
    const token = process.env.REACT_APP_MY_API_TOKEN;
    const myApiUrl = `${process.env.REACT_APP_MY_API_URL}/api/my_connect_user/${userId}?token=${token}`;

    try {
        const response = await fetch(myApiUrl, {
            method: 'GET',
            redirect: 'manual',
        });
        const redirectUrl = response.url;

        if (redirectUrl) {
            try {
                await fetch(`${process.env.REACT_APP_NEWS_FACTORY_API_URL}/api/logout`, {
                    method: 'POST',
                    credentials: 'include',
                });
            } catch (error) {
                console.error('Erreur lors du logout API :', error);
            }

            // Suppression des données locales
            localStorage.clear();
            // Redirection vers My Previsite
            window.location.href = redirectUrl;
            return true;
        } else {
            console.error('Échec de la connexion SSO', response);
            return false;
        }
    } catch (error) {
        console.error('Erreur lors de la requête SSO :', error);
        return false;
    }
};
